/**
 * CR-09 `/performance` and CR-10 `/earnings`.
 *
 * Two screens with one job between them: tell a creator what they are owed
 * and why, without ever saying something the platform does not know.
 *
 * Three things they refuse to do, each because a reader would otherwise
 * draw a false conclusion:
 *
 *   * They never add currencies. Each currency is its own block with its
 *     own total, and there is no grand total anywhere, because there is no
 *     number that is the sum of GBP and USD (FR-FIN-033).
 *   * They never print a rate with no denominator. A post with no eligible
 *     views shows "-" for effective CPM, not 0.00 (TC-047).
 *   * They never print a missing measurement as zero. A blank views figure
 *     says the last collection did not return one, and says which failure
 *     it was, because a zero would be a claim that the post has no views
 *     (TC-011).
 *
 * The figures are not computed here. They come from the projections, so a
 * creator and a brand reading the same post read the same numbers.
 */
import { useEffect, useState } from "react";
import {
  PageHeader, StatTile, StateBlock, Money, Card, Eyebrow, Badge, LinkButton,
} from "@ui/primitives";
import { fontSize, space } from "@design/index";
import { fromMinorString, type CurrencyCode } from "@contracts/money";
import { api } from "@app/shared/api";
import { CardListSkeleton, TileRowSkeleton } from "@app/shared/Skeletons";
import { claimLabel, claimTone, formatViews } from "@app/shared/EarningClaim";
import { platformMeta } from "@app/shared/Platforms";
import { CreatorStats } from "./CreatorStats";

interface Balance {
  currency: string; pending_minor: string; available_minor: string;
  held_minor: string; reserved_minor: string; in_transit_minor: string;
  total_minor: string; lots: number;
}

interface Lot {
  lot_id: string; campaign_id: string; campaign_name: string | null;
  submission_id: string | null; currency: string; amount_minor: string;
  pending_minor: string; available_minor: string; held_minor: string;
  reserved_minor: string; in_transit_minor: string;
  journal_entry_id: string; created_at: string;
}

interface Adjustment {
  request_id: string; currency: string; amount_minor: string; reason: string;
  source_ref_type: string; entry_id: string; posted_at: string;
}

interface Claim {
  id: string; submission_id: string; campaign_id: string; campaign_name: string | null;
  platform: string; submitted_url: string; views: string;
  amount_minor: string; currency: string; state: string;
  requested_at: string; reviewed_at: string | null; review_note: string | null;
}

interface Performance {
  submission_id: string; campaign_id: string; campaign_name: string | null;
  platform: string; post_id: string | null;
  submission_state: string; moderation_state: string; availability: string;
  currency: string; cpm_gross_minor: string; gross_cap_minor: string;
  reported_views: string | null; reported_at: string | null;
  eligible_views: string | null; eligible_at: string | null;
  excluded_views: string | null; exclusion_reason: string | null;
  posted_minor: string; cap_applied: boolean;
  effective_cpm_minor: string | null;
  last_outcome: string | null; last_success_at: string | null; last_error: string | null;
  measurement_cutoff_at: string | null;
}

const Amount = ({ minor, currency, emphasis = false }: {
  minor: string; currency: string; emphasis?: boolean;
}) => <Money value={fromMinorString(minor, currency as CurrencyCode)} emphasis={emphasis} />;

/** A view count, or an honest blank. Never a zero standing in for silence. */
function Views({ value, unknown = "Not reported" }: { value: string | null; unknown?: string }) {
  if (value === null || value === undefined) {
    return <span style={{ color: "var(--faint)" }} title={unknown}>-</span>;
  }
  return <span style={{ fontVariantNumeric: "tabular-nums" }}>
    {Number(value).toLocaleString("en-GB")}
  </span>;
}

function when(value: string | null) {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toISOString().slice(0, 16).replace("T", " ");
}

/* ------------------------------------------------------------ earnings */

export function EarningsScreen() {
  const [data, setData] = useState<
    { balances: Balance[]; lots: Lot[]; adjustments: Adjustment[]; claims?: Claim[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.get<{ balances: Balance[]; lots: Lot[]; adjustments: Adjustment[]; claims?: Claim[] }>(
      "/earnings").then((r) => {
        if (r.ok) setData(r.data); else setError(r.message);
      });
  }, []);

  return (
    <>
      <PageHeader
        eyebrow="Earnings"
        title="What you have earned"
        description="Views first. Claim at 1,000 counted views. The brand reviews the claim, then the amount lands here - pending, then available. Currencies are never added together."
      />

      {error ? <StateBlock kind="error" title="Could not load your earnings" detail={error} />
        : data === null ? (
          <div style={{ display: "grid", gap: space(4) }}>
            <TileRowSkeleton count={5} />
            <CardListSkeleton count={2} />
          </div>
        )
        : data.balances.length === 0 && !(data.claims ?? []).length ? (
          <StateBlock kind="empty" title="No earnings yet"
            detail="Approve a post, wait for counted views, then claim from Your submission once it passes 1,000. The brand reviews that claim before money appears here."
            action={<LinkButton href="/work" variant="primary" size="sm">Open My Campaigns</LinkButton>} />
        ) : (
          <div style={{ display: "grid", gap: space(5) }}>
            {(data.claims ?? []).length > 0 ? (
              <section style={{ display: "grid", gap: space(2) }}>
                <Eyebrow>Claims</Eyebrow>
                <div style={{ display: "grid", gap: 10 }}>
                  {(data.claims ?? []).map((cl) => (
                    <Card key={cl.id} padding={18} style={{
                      display: "grid", gap: 10,
                      gridTemplateColumns: "minmax(160px, 1.4fr) minmax(120px, 1fr) auto",
                      alignItems: "center",
                    }}>
                      <div style={{ display: "grid", gap: 4 }}>
                        <strong style={{ fontSize: fontSize.bodyLg }}>
                          {cl.campaign_name ?? "Campaign"}
                        </strong>
                        <span style={{ fontSize: fontSize.label, color: "var(--muted)" }}>
                          {cl.platform} · {formatViews(cl.views)} views
                        </span>
                      </div>
                      <Amount minor={cl.amount_minor} currency={cl.currency} emphasis />
                      <div style={{ display: "grid", gap: 4, justifyItems: "end" }}>
                        <Badge tone={claimTone(cl.state)}>{claimLabel(cl.state)}</Badge>
                        <span style={{ fontSize: fontSize.micro, color: "var(--faint)" }}>
                          {when(cl.requested_at)}
                        </span>
                      </div>
                      {cl.review_note ? (
                        <p style={{
                          gridColumn: "1 / -1", margin: 0,
                          fontSize: fontSize.label, color: "var(--text-2)", lineHeight: 1.5,
                        }}>
                          {cl.review_note}
                        </p>
                      ) : null}
                    </Card>
                  ))}
                </div>
              </section>
            ) : null}
            {data.balances.map((b) => (
              <section key={b.currency} aria-label={`Balances in ${b.currency}`}
                       style={{ display: "grid", gap: space(2) }}>
                <Eyebrow>Balances · {b.currency}</Eyebrow>
                <div style={{ display: "grid", gap: space(2),
                              gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
                  <StatTile label="Available" tone="ok"
                    value={<Amount minor={b.available_minor} currency={b.currency} emphasis />}
                    hint="Ready to withdraw" />
                  <StatTile label="Pending"
                    value={<Amount minor={b.pending_minor} currency={b.currency} />}
                    hint="Earned, inside its hold period or awaiting review" />
                  <StatTile label="Reserved" tone="process"
                    value={<Amount minor={b.reserved_minor} currency={b.currency} />}
                    hint="Set aside against a payout you requested" />
                  <StatTile label="Held" tone="warn"
                    value={<Amount minor={b.held_minor} currency={b.currency} />}
                    hint="Under a review. Still yours, not yet movable" />
                  <StatTile label="In transit" tone="process"
                    value={<Amount minor={b.in_transit_minor} currency={b.currency} />}
                    hint="Sent to your bank, not yet confirmed as credited" />
                </div>
                <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--muted)",
                            lineHeight: 1.6 }}>
                  Total owed to you in {b.currency}:{" "}
                  <strong><Amount minor={b.total_minor} currency={b.currency} /></strong>{" "}
                  across {b.lots} earning{b.lots === 1 ? "" : "s"}. Held is part of that
                  total, not an extra amount on top of it.
                </p>
              </section>
            ))}

            <Card padding={18} style={{ display: "grid", gap: space(2) }}>
              <Eyebrow>Where it came from</Eyebrow>
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%",
                                fontSize: fontSize.label }}>
                  <thead>
                    <tr>
                      {["Campaign", "Earned", "Available", "Pending", "Held", "When"]
                        .map((h, i) => (
                        <th key={h} style={{
                          textAlign: i === 0 || i === 5 ? "left" : "right",
                          padding: "8px 12px 8px 0", borderBottom: "1px solid var(--line)",
                          color: "var(--muted)", fontWeight: 600, whiteSpace: "nowrap",
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.lots.map((l) => (
                      <tr key={l.lot_id}>
                        <td style={cell()}>{l.campaign_name ?? "-"}</td>
                        <td style={cell(true)}>
                          <Amount minor={l.amount_minor} currency={l.currency} emphasis /></td>
                        <td style={cell(true)}>
                          <Amount minor={l.available_minor} currency={l.currency} /></td>
                        <td style={cell(true)}>
                          <Amount minor={l.pending_minor} currency={l.currency} /></td>
                        <td style={cell(true)}>
                          <Amount minor={l.held_minor} currency={l.currency} /></td>
                        <td style={cell()}>{when(l.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {data.adjustments.length > 0 ? (
              <Card padding={18} style={{ display: "grid", gap: space(2) }}>
                <Eyebrow>Corrections</Eyebrow>
                <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--muted)",
                            lineHeight: 1.6 }}>
                  A correction to your balance, with the reason it was made. Two
                  authorized people signed each of these. If one looks wrong, open a
                  support case and it will be reviewed.
                </p>
                <ul style={{ margin: 0, padding: 0, listStyle: "none",
                             display: "grid", gap: space(2) }}>
                  {data.adjustments.map((a) => (
                    <li key={a.request_id} style={{ fontSize: fontSize.body }}>
                      <Amount minor={a.amount_minor} currency={a.currency} emphasis />
                      {" · "}{a.reason}
                      <div style={{ color: "var(--muted)", fontSize: fontSize.micro }}>
                        {when(a.posted_at)}
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}
          </div>
        )}
    </>
  );
}

const cell = (numeric = false) => ({
  textAlign: numeric ? "right" as const : "left" as const,
  padding: "10px 12px 10px 0",
  borderBottom: "1px solid var(--line-soft)",
  verticalAlign: "top" as const,
});

/* --------------------------------------------------------- performance */

export function PerformanceScreen() {
  const [rows, setRows] = useState<Performance[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.get<{ submissions: Performance[] }>("/performance").then((r) => {
      if (r.ok) setRows(r.data.submissions); else setError(r.message);
    });
  }, []);

  return (
    <>
      <header style={{ display: "grid", gap: 16 }}>
        <p style={{
          margin: 0, fontSize: 13, fontWeight: 650, color: "var(--muted)", letterSpacing: "0.02em",
        }}>
          Performance
        </p>
        <h1 className="cm-campaign-title" style={{
          margin: 0, fontSize: "clamp(28px, 3.4vw, 42px)", fontWeight: 800,
          letterSpacing: "-0.035em", lineHeight: 1.08,
        }}>
          How your posts are measured
        </h1>
        <p style={{ margin: 0, fontSize: 16, color: "var(--text-2)", lineHeight: 1.6, maxWidth: 640 }}>
          What the platform reported, what counted towards your earnings, and what did not - with the reason.
        </p>
      </header>

      <CreatorStats />

      {error ? <StateBlock kind="error" title="Could not load your performance" detail={error} />
        : rows === null ? <CardListSkeleton count={3} />
        : rows.length === 0 ? (
          <StateBlock kind="empty" title="Nothing measured yet"
            detail="A post is measured once the brand approves it and the campaign's route is collecting. A missing view count is a dash, never a zero."
            action={<LinkButton href="/work" variant="primary" size="sm">Open My Campaigns</LinkButton>} />
        ) : (
          <div style={{ display: "grid", gap: space(4) }}>
            {rows.map((p) => {
              const platformLabel = p.platform ? platformMeta(p.platform).label : null;
              return (
              <Card key={p.submission_id} padding={18} style={{ display: "grid", gap: space(3) }}>
                <div style={{ display: "flex", justifyContent: "space-between",
                              alignItems: "baseline", gap: space(3), flexWrap: "wrap" }}>
                  <h2 className="cm-campaign-section-title">
                    {p.campaign_name ?? "Campaign"}
                    {platformLabel ? ` · ${platformLabel}` : ""}
                  </h2>
                  <div style={{ display: "flex", gap: space(2), flexWrap: "wrap" }}>
                    {p.cap_applied ? <Badge tone="warn">Cap reached</Badge> : null}
                    {p.moderation_state !== "CLEAR"
                      ? <Badge tone="warn">Under moderation</Badge> : null}
                    {p.last_outcome && p.last_outcome !== "OK"
                      ? <Badge tone="warn">Measurement stale</Badge> : null}
                  </div>
                </div>

                <div style={{ display: "grid", gap: space(2),
                              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
                  <StatTile label="Reported views"
                    value={<Views value={p.reported_views} />}
                    hint={p.reported_views === null
                      ? "The last collection returned nothing"
                      : `As at ${when(p.reported_at)}`} />
                  <StatTile label="Counted views" tone="ok"
                    value={<Views value={p.eligible_views} />}
                    hint="What your earnings are calculated from" />
                  <StatTile label="Not counted"
                    value={<Views value={p.excluded_views} />}
                    hint={p.exclusion_reason ?? "Nothing excluded"} />
                  <StatTile label="Earned" tone="ok"
                    value={<Amount minor={p.posted_minor} currency={p.currency} emphasis />}
                    hint="Posted to your balance" />
                  <StatTile label="Rate earned"
                    value={p.effective_cpm_minor === null
                      ? <span style={{ color: "var(--faint)" }}>-</span>
                      : <Amount minor={String(Math.round(Number(p.effective_cpm_minor)))}
                                currency={p.currency} />}
                    hint={p.effective_cpm_minor === null
                      ? "No counted views yet, so there is no rate to show"
                      : "Per 1,000 counted views"} />
                </div>

                {p.exclusion_reason ? (
                  <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--warn-fg)",
                              lineHeight: 1.6 }}>
                    {p.exclusion_reason}
                  </p>
                ) : null}

                <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--muted)",
                            lineHeight: 1.6 }}>
                  {platformLabel ? `${platformLabel} rate ` : "Rate "}
                  <Amount minor={p.cpm_gross_minor} currency={p.currency} /> per 1,000 ·
                  {" "}cap <Amount minor={p.gross_cap_minor} currency={p.currency} /> ·
                  {" "}last measured {when(p.last_success_at)}
                  {p.last_error ? ` · ${p.last_error}` : ""}
                </p>
              </Card>
              );
            })}

            <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--faint)",
                        lineHeight: 1.6 }}>
              Reported views are what the platform told us. They are not a count of
              unique people, and a blank figure means the last collection did not
              return one - never that your post has no views.
            </p>
          </div>
        )}
    </>
  );
}
